// Dexie schema relais : snapshots tâches + renders + medias + queue mutations.
// Patterns Safari/WebKit (withRetry, withTimeout, safeOfflineDb) portés de greenkub.

import { Dexie, type Table } from "dexie";
import type {
  RenderMediaKind,
  SyncMutation,
  Task,
  TaskListUser,
  TaskRender,
} from "@relais/shared";

export type OfflineTaskRecord = {
  id: string;
  userId: string; // partition key (user logué) — équivalent du conductorEmail greenkub
  data: Task;
  // Référent (superviseur) mis en cache depuis le snapshot liste pour rester
  // consultable hors-ligne. Champ non indexé : préservé à travers les writes
  // (les payloads serveur de flush ne le portent pas, cf. upsertTasks/markCompleted).
  supervisor?: TaskListUser;
  updatedAt: string;
  localStatus: "clean" | "dirty" | "conflict";
};

export type OfflineRenderRecord = {
  taskId: string;
  userId: string;
  data: TaskRender | null;
  updatedAt: string;
};

export type OfflineMediaRecord = {
  id: string; // `${taskId}:${clientId}`
  taskId: string;
  clientId: string;
  userId: string;
  kind: RenderMediaKind;
  mimeType: string;
  sizeBytes: number;
  caption?: string;
  blobData?: string; // base64, sans préfixe data:
  storagePath?: string; // rempli quand uploadé
  uploaded: boolean;
  createdAt: string;
  updatedAt: string;
  uploadRetryCount?: number;       // undefined treated as 0
  uploadLastError?: string;
  uploadLastAttemptAt?: string;    // ISO, drives backoff
};

export type QueueStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed"
  | "purged"
  | "conflict";

export type OfflineQueueRecord = {
  id: string; // uuid local
  userId: string;
  taskId: string;
  kind: SyncMutation["kind"];
  payload: SyncMutation;
  priority: number; // plus petit = plus prioritaire
  status: QueueStatus;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
  lastError?: string;
};

export type OfflineCatalogueRecord = {
  name: string;            // primary key
  version: string;
  fetchedAt: string;       // ISO
  data: unknown;           // serialised UpsellOption[]
};

/**
 * Petits drapeaux internes au client (pas de la donnée métier) : sert
 * aujourd'hui à mémoriser qu'un dégonflage de blobs a été mené à son terme pour
 * un utilisateur, pour ne pas re-balayer tout le store à chaque démarrage.
 */
export type OfflineMetaRecord = {
  key: string;
  value: unknown;
};

export class RelaisOfflineDb extends Dexie {
  tasks!: Table<OfflineTaskRecord, string>;
  renders!: Table<OfflineRenderRecord, string>;
  medias!: Table<OfflineMediaRecord, string>;
  queue!: Table<OfflineQueueRecord, string>;
  catalogues!: Table<OfflineCatalogueRecord, string>;
  meta!: Table<OfflineMetaRecord, string>;

  private _isClosed = false;

  constructor(name = "RelaisOfflineDB") {
    super(name);

    this.on("close", () => {
      this._isClosed = true;
    });

    this.version(1).stores({
      tasks: "id,userId,updatedAt,[userId+localStatus]",
      renders: "taskId,userId,updatedAt",
      medias: "id,taskId,userId,uploaded,updatedAt",
      queue: "id,userId,taskId,status,priority,createdAt,[userId+status]",
      catalogues: "name,fetchedAt",
    });

    // v2 — AJOUT d'un store vide, et rien d'autre.
    //
    // L'index `uploaded` de `medias` est mort-né (IndexedDB refuse les booléens
    // comme clés : il n'a jamais indexé une seule ligne, d'où le filtrage en JS
    // qui désérialisait tous les blobs). Il mériterait d'être retiré — mais le
    // retirer ferait porter la transaction de version sur le store médias, sur
    // des appareils dont le backend IndexedDB rend déjà `UnknownError` sous la
    // charge. Une transaction de version qui échoue rend la base IMPOSSIBLE à
    // ouvrir : l'app ne serait plus lente, elle serait morte, et les photos
    // pas encore synchronisées deviendraient inatteignables.
    //
    // Créer un store vide ne touche aucune donnée existante. On accepte donc de
    // garder un index inutile — documenté ici — plutôt que de risquer la base
    // d'un relais pour du ménage cosmétique. À retirer quand le parc sera
    // dégonflé (reclaimUploadedBlobs) et les bases revenues à une taille saine.
    this.version(2).stores({
      meta: "key",
    });

    if (typeof document !== "undefined") {
      this.setupLifecycleListeners();
    }
  }

  static isIndexedDBAvailable(): boolean {
    try {
      return (
        typeof globalThis !== "undefined" &&
        typeof (globalThis as { indexedDB?: unknown }).indexedDB !== "undefined" &&
        (globalThis as { indexedDB?: unknown }).indexedDB !== null
      );
    } catch {
      return false;
    }
  }

  async ensureOpen(): Promise<void> {
    if (!RelaisOfflineDb.isIndexedDBAvailable()) return;
    if (!this.isOpen() || this._isClosed) {
      this._isClosed = false;
      await this.open();
    }
  }

  // Duck-typing plutôt qu'`instanceof Error` : les erreurs remontées par Dexie
  // enveloppent des DOMException et ne traversent pas toujours le test de
  // prototype (realm différent, wrapper Dexie). On ne lit que name/message.
  private static errorShape(error: unknown): { name: string; msg: string } {
    const e = (error ?? {}) as { name?: unknown; message?: unknown };
    return {
      name: typeof e.name === "string" ? e.name : "",
      msg: typeof e.message === "string" ? e.message : "",
    };
  }

  static isDatabaseClosedError(error: unknown): boolean {
    const { name, msg } = RelaisOfflineDb.errorShape(error);
    return (
      name === "DatabaseClosedError" ||
      msg.includes("DatabaseClosedError") ||
      msg.includes("database connection is closing") ||
      msg.includes("Connection to Indexed Database server lost")
    );
  }

  /**
   * Panne du backend IndexedDB de WebKit. Safari rend un `UnknownError`
   * (« An internal error was encountered in the Indexed Database server »)
   * quand son processus de stockage lâche sous la charge — observé en prod le
   * 2026-09-21 sur un iPad dont la base avait enflé à 8 Go. Dexie rouvre la
   * connexion de son côté (« Need to reopen db ») : sans cette classe ici,
   * `withRetry` traitait l'erreur comme fatale et abandonnait l'opération alors
   * qu'un simple retry après réouverture passe.
   */
  static isBackendFailureError(error: unknown): boolean {
    const { name, msg } = RelaisOfflineDb.errorShape(error);
    return (
      name === "UnknownError" ||
      msg.includes("An internal error was encountered in the Indexed Database server")
    );
  }

  private static isRecoverable(error: unknown): boolean {
    return (
      RelaisOfflineDb.isDatabaseClosedError(error) ||
      RelaisOfflineDb.isBackendFailureError(error) ||
      RelaisOfflineDb.errorShape(error).name === "IndexedDBTimeoutError"
    );
  }

  private static TIMEOUT_MS = 5000;

  private withTimeout<T>(op: () => Promise<T>, ms = RelaisOfflineDb.TIMEOUT_MS): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      const t = setTimeout(() => {
        if (settled) return;
        settled = true;
        const err = new Error(`IndexedDB operation timed out after ${ms}ms`);
        err.name = "IndexedDBTimeoutError";
        reject(err);
      }, ms);
      op().then(
        (v) => {
          if (settled) return;
          settled = true;
          clearTimeout(t);
          resolve(v);
        },
        (e) => {
          if (settled) return;
          settled = true;
          clearTimeout(t);
          reject(e);
        },
      );
    });
  }

  async withRetry<T>(op: () => Promise<T>): Promise<T> {
    try {
      return await this.withTimeout(op);
    } catch (err) {
      if (!RelaisOfflineDb.isRecoverable(err)) throw err;
      await this.ensureOpen();
      try {
        return await this.withTimeout(op);
      } catch (err2) {
        if (!RelaisOfflineDb.isRecoverable(err2)) throw err2;
        await new Promise((r) => setTimeout(r, 100));
        await this.ensureOpen();
        return this.withTimeout(op);
      }
    }
  }

  private setupLifecycleListeners(): void {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        this.ensureOpen().catch(() => undefined);
      }
    });
    window.addEventListener("pageshow", (ev) => {
      if ((ev as PageTransitionEvent).persisted) {
        this.ensureOpen().catch(() => undefined);
      }
    });
  }
}

// Singleton lazy
let _db: RelaisOfflineDb | null = null;
let _configuredName: string | null = null;

/**
 * Configure the IndexedDB name for the offline database.
 * Must be called before the first getDb() call, or will throw if attempting
 * to configure a different name after the singleton has been instantiated.
 *
 * @param options - Configuration options
 * @param options.name - The IndexedDB database name to use
 * @throws Error if singleton exists with a different name
 */
export function configureOfflineDb(options: { name: string }): void {
  if (_db !== null && _db.name !== options.name) {
    throw new Error(
      `Cannot reconfigure database name: already instantiated with name "${_db.name}", ` +
      `cannot change to "${options.name}"`,
    );
  }
  _configuredName = options.name;
}

export function getDb(): RelaisOfflineDb {
  if (!_db) {
    const dbName = _configuredName ?? "RelaisOfflineDB";
    _db = new RelaisOfflineDb(dbName);
  }
  return _db;
}

export function resetDbForTests(): void {
  _db = null;
  _configuredName = null;
}
